import styled from "styled-components";

export const Container = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 2.4rem;

  position: fixed;
  top: 1.2rem;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: min(95%, 1400px);
  z-index: 1000;
  border-radius: 14px;

  /* Developer-dark glass — Catppuccin surface with mauve border */
  background: rgba(30, 30, 46, 0.78);
  border: 1px solid rgba(198, 160, 246, 0.14);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(202, 211, 245, 0.06);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    backdrop-filter: blur(20px) saturate(160%);
    z-index: -1;
    pointer-events: none;
  }

  transition: all 0.3s ease;

  nav {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-left: auto;

    a {
      /* Nav links in monospace — purposeful structural use */
      color: var(--text-muted);
      padding: 0.6rem 1.4rem;
      border-radius: 8px;
      font-family: var(--font-code);
      font-weight: 500;
      font-size: 1.25rem;
      letter-spacing: 0.04em;
      transition: color 0.2s ease, background 0.2s ease;
      position: relative;

      &:hover {
        color: var(--text-primary);
        background: rgba(198, 160, 246, 0.08);
      }

      &.nav-active {
        color: var(--accent-mauve);
        background: rgba(198, 160, 246, 0.12);
        box-shadow: inset 0 0 0 1px rgba(198, 160, 246, 0.2);
      }

      &.button {
        padding: 0.7rem 2.2rem;
        margin-left: 0.8rem;
        border-radius: 8px;
        background: var(--accent-mauve);
        color: var(--bg-base) !important;
        font-family: var(--font-code);
        font-size: 1.25rem;
        font-weight: 600;
        box-shadow: 0 4px 14px rgba(198, 160, 246, 0.3);
        border: none;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(198, 160, 246, 0.45);
          opacity: 0.9;
          color: var(--bg-base) !important;
        }
      }
    }
  }

  .logo {
    display: flex;
    align-items: center;

    .logo-image {
      height: 54px;
      width: auto;
      object-fit: contain;
      border-radius: 10px;
      transition: transform 0.3s ease, filter 0.3s ease;

      &:hover {
        transform: scale(1.06);
        filter: drop-shadow(0 0 8px rgba(198, 160, 246, 0.5));
      }
    }
  }

  .menu {
    width: 2rem;
    height: 0.2rem;
    background: var(--text-primary);
    position: relative;
    cursor: pointer;
    display: none;

    &:before { bottom: 0.5rem; }
    &:after  { top: 0.5rem; }

    &.active:before { bottom: 0; transform: rotate(45deg); }
    &.active:after  { top: 0;    transform: rotate(135deg); }
    &.active        { background-color: transparent; }
  }

  .menu:before, .menu:after {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 0.2rem;
    background: var(--text-primary);
    cursor: pointer;
    transition: 0.5s;
  }

  .theme-btn {
    background: rgba(198, 160, 246, 0.08);
    border: 1px solid rgba(198, 160, 246, 0.18);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    cursor: pointer;
    margin-left: 0.8rem;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(198, 160, 246, 0.15);
      border-color: var(--accent-mauve);
      color: var(--accent-mauve);
      transform: scale(1.05);
    }
  }

  @media (max-width: 960px) {
    width: min(94%, 800px);
    padding: 0.65rem 1.25rem;
    top: 0.8rem;

    .theme-btn {
      margin-left: auto;
      margin-right: 1.5rem;
      z-index: 1002;
    }

    .menu {
      display: block;
      padding: 14px 0;
      background-clip: content-box;
      margin-left: 0.25rem;
      z-index: 1002;

      &:before { bottom: 1.1rem; }
      &:after  { top:    1.1rem; }
      &.active:before { bottom: 0.7rem; transform: rotate(45deg); }
      &.active:after  { top:    0.7rem; transform: rotate(135deg); }
    }

    nav {
      -ms-overflow-style: none;
      scrollbar-width: none;
      overflow-y: auto;
      opacity: 0;
      visibility: hidden;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 2rem;
      position: fixed;
      inset: 0;
      width: 100dvw;
      height: 100dvh;
      padding: 4rem 2rem;
      border-radius: 0;
      background: rgba(30, 30, 46, 0.97);
      -webkit-backdrop-filter: blur(28px);
      backdrop-filter: blur(28px);
      border: none;
      box-shadow: none;
      transform: scale(0.96);
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      z-index: 1001;

      a {
        font-family: var(--font-code);
        font-size: 2rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        padding: 0.9rem 2.4rem;
        width: auto;
        text-align: center;
        border-radius: 10px;
        color: var(--text-muted);
        transition: all 0.2s ease;

        &:hover {
          color: var(--text-primary);
          background: rgba(198, 160, 246, 0.1);
        }

        &.nav-active {
          color: var(--accent-mauve);
          background: rgba(198, 160, 246, 0.12);
          box-shadow: inset 0 0 0 1px rgba(198, 160, 246, 0.2);
        }

        &.button {
          margin-left: 0;
          margin-top: 0.5rem;
          min-width: 200px;
          padding: 1rem 3rem;
          font-size: 2rem;
          background: var(--accent-mauve);
          color: var(--bg-base) !important;
          box-shadow: 0 8px 24px rgba(198, 160, 246, 0.4);
        }
      }

      &.active {
        opacity: 1;
        visibility: visible;
        transform: scale(1);
      }
    }
  }

  @media (max-width: 600px) {
    width: 94%;
    padding: 0.55rem 1rem;
    top: 0.6rem;
    border-radius: 12px;

    .logo .logo-image { height: 44px; }

    .theme-btn {
      margin-left: auto;
      margin-right: 1.25rem;
    }

    .menu { margin-left: 0.2rem; }
  }
`;